/**
 * SignatureService
 * Handles signature validation, processing, and storage
 */

import { createLogger, getErrorMessage } from '../logger';

const logger = createLogger('SignatureService');

export interface DigitalSignature {
  type: 'digital';
  signatureData: string; // Base64 encoded image
  timestamp: string;
  userAgent: string;
}

export interface PhysicalSignature {
  type: 'physical';
  fileName: string;
  fileSize: number;
  timestamp: string;
  userAgent: string;
}

export type Signature = DigitalSignature | PhysicalSignature;

export class SignatureService {
  /**
   * Validates a digital signature (base64 image)
   */
  static validateDigitalSignature(signatureData: string): boolean {
    try {
      // Check if it's a valid base64 image data URL
      const imageDataUrlPattern = /^data:image\/[a-zA-Z0-9.+-]+;base64,/;
      if (!imageDataUrlPattern.test(signatureData)) {
        return false;
      }

      // Check if base64 data exists
      const base64Data = signatureData.split(',')[1];
      if (!base64Data || base64Data.length === 0) {
        return false;
      }

      // Decode base64 to check if valid
      atob(base64Data);

      return true;
    } catch (error) {
      logger.error('Invalid signature data:', {
        error: getErrorMessage(error, 'Invalid signature data'),
      });
      return false;
    }
  }

  /**
   * Validates a physical signature file
   */
  static validatePhysicalSignatureFile(file: File): {
    valid: boolean;
    error?: string;
  } {
    // Check file type
    if (file.type !== 'application/pdf') {
      return { valid: false, error: 'File must be a PDF' };
    }

    // Check file size (max 10MB)
    const maxSize = 10 * 1024 * 1024; // 10MB
    if (file.size > maxSize) {
      return { valid: false, error: 'File size must be less than 10MB' };
    }

    // Check file name
    if (!file.name || file.name.length === 0) {
      return { valid: false, error: 'Invalid file name' };
    }

    return { valid: true };
  }

  /**
   * Creates a digital signature object
   */
  static createDigitalSignature(signatureData: string): DigitalSignature {
    if (!this.validateDigitalSignature(signatureData)) {
      throw new Error('Invalid signature data');
    }

    return {
      type: 'digital',
      signatureData,
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent,
    };
  }

  /**
   * Creates a physical signature object
   */
  static createPhysicalSignature(file: File): PhysicalSignature {
    const validation = this.validatePhysicalSignatureFile(file);
    if (!validation.valid) {
      throw new Error(validation.error || 'Invalid file');
    }

    return {
      type: 'physical',
      fileName: file.name,
      fileSize: file.size,
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent,
    };
  }

  /**
   * Converts signature to JSON for storage
   */
  static serializeSignature(signature: Signature): string {
    return JSON.stringify(signature);
  }

  /**
   * Parses signature from JSON
   */
  static deserializeSignature(jsonString: string): Signature {
    try {
      const signature = JSON.parse(jsonString) as Signature;

      if (!signature.type || !signature.timestamp) {
        throw new Error('Invalid signature structure');
      }

      return signature;
    } catch (error) {
      logger.error('Failed to parse signature data:', {
        error: getErrorMessage(error, 'Failed to parse signature data'),
      });
      throw new Error('Failed to parse signature data');
    }
  }

  /**
   * Generates a signature summary for display
   */
  static getSignatureSummary(signature: Signature): string {
    const date = new Date(signature.timestamp).toLocaleString();

    if (signature.type === 'digital') {
      return `Digital signature captured on ${date}`;
    } else {
      return `Physical signature uploaded on ${date} (${signature.fileName})`;
    }
  }
}
